// What one scenario costs at the hospitals around the reader — shared by the
// scenario page's "near you" card block and the /scenarios listing cards, so
// the two can never disagree (Trent, 2026-09-29).
//
// Blended bills come from the per-scenario coverage shard (the hospital's own
// prices, any code it doesn't publish filled at the U.S. average); garbage
// totals and hidden duplicates are out (the same filters the page's hospital
// list applies); "cheapest" passes the shared candidate gate and "priciest" the
// shared here-gate from lib/localSavings; the drive verdict is the shared one.
import { useEffect, useMemo, useState } from "react";
import { candidatePlausible, driveVerdict, herePlausible, type DriveVerdict } from "@/lib/localSavings";
import { isGarbagePrice } from "@/lib/priceSanity";
import { getGrade } from "@/lib/grades";
import { getHospitalInfo, haversineDistance, loadScenarioCoverageFor, type CoverageRow, type HospitalInfo } from "@/lib/scenarioCoverageStore";
import type { UserLocation } from "@/hooks/useUserLocation";

export const NEAR_RADII = [30, 60, 90, 120] as const;
export const MIN_NEARBY = 5;

export interface LocalHospital {
  id: string; info: HospitalInfo; miles: number; price: number;
  quotable: boolean;            // passes the shared candidate gate (safe to quote as "cheapest")
  cheaperThanPct: number;       // share of U.S. hospitals pricing this higher
  rank: number; ranked: number; // 1 = cheapest in the U.S.
  /** The site's scenario grade (getGrade on the blended markup vs the U.S. average) — the same letter Atlas and the embed show. */
  letter: "A" | "B" | "C" | "D" | "F";
}

function blended(row: CoverageRow, nationalTotal: number): number {
  const mc = row.mc ?? 0, tc = row.tc ?? 0;
  if (mc >= tc || nationalTotal <= 0) return row.hp;
  return row.hp + Math.max(0, nationalTotal - (row.np ?? 0));
}

/** Plain-language national position of one blended bill. */
export function rankText(l: LocalHospital): string {
  return l.cheaperThanPct >= 50
    ? `Cheaper than ${l.cheaperThanPct}% of U.S. hospitals`
    : `Pricier than ${100 - l.cheaperThanPct}% of U.S. hospitals`;
}

export function useLocalScenarioPrices(scenarioId: string, nationalTotal: number, location: UserLocation | null, radius: number, enabled = true) {
  const [rows, setRows] = useState<CoverageRow[] | null>(null);

  useEffect(() => {
    if (!enabled || !location) return;
    let dead = false;
    setRows(null);
    loadScenarioCoverageFor(scenarioId).then((r) => { if (!dead) setRows(r); }).catch(() => { if (!dead) setRows([]); });
    return () => { dead = true; };
    // location only gates the first load; the shard doesn't depend on it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId, enabled, !!location]);

  // Every U.S. hospital's blended bill for this scenario — the rank pool.
  const pool = useMemo(() => {
    if (!rows) return null;
    const items: { row: CoverageRow; info: HospitalInfo; price: number }[] = [];
    for (const row of rows) {
      const info = getHospitalInfo(row.h);
      if (!info) continue;
      const price = blended(row, nationalTotal);
      const markup = nationalTotal > 0 ? ((price - nationalTotal) / nationalTotal) * 100 : null;
      if (isGarbagePrice(price, nationalTotal, markup)) continue;
      items.push({ row, info, price });
    }
    return { items, sorted: items.map((o) => o.price).sort((a, b) => a - b) };
  }, [rows, nationalTotal]);

  // Everything within the widest radius, nearest first.
  const all = useMemo<LocalHospital[]>(() => {
    if (!pool || !location) return [];
    const s = pool.sorted, n = s.length;
    const lower = (v: number) => { let lo = 0, hi = n; while (lo < hi) { const m = (lo + hi) >> 1; if (s[m] < v) lo = m + 1; else hi = m; } return lo; };
    const upper = (v: number) => { let lo = 0, hi = n; while (lo < hi) { const m = (lo + hi) >> 1; if (s[m] <= v) lo = m + 1; else hi = m; } return lo; };
    const out: LocalHospital[] = [];
    for (const { row, info, price } of pool.items) {
      if (info.la == null || info.lo == null) continue;
      const miles = haversineDistance(location.lat, location.lng, info.la, info.lo);
      if (miles > NEAR_RADII[NEAR_RADII.length - 1]) continue;
      out.push({
        id: row.h, info, miles, price, quotable: candidatePlausible(price, nationalTotal),
        cheaperThanPct: Math.round(((n - upper(price)) / n) * 100), rank: lower(price) + 1, ranked: n,
        letter: getGrade(nationalTotal > 0 ? ((price - nationalTotal) / nationalTotal) * 100 : 0),
      });
    }
    return out.sort((a, b) => a.miles - b.miles);
  }, [pool, location, nationalTotal]);

  const local = useMemo(() => all.filter((l) => l.miles <= radius), [all, radius]);
  const nearest = local[0] ?? null;
  const cheapest = useMemo(() => local.filter((l) => l.quotable).reduce<LocalHospital | null>((m, l) => (!m || l.price < m.price ? l : m), null), [local]);
  const priciest = useMemo(() => local.filter((l) => herePlausible(l.price, nationalTotal)).reduce<LocalHospital | null>((m, l) => (!m || l.price > m.price ? l : m), null), [local, nationalTotal]);
  const verdictOf = (l: LocalHospital): DriveVerdict | null =>
    (nearest && l.id !== nearest.id ? driveVerdict(nearest.price, l.price, nationalTotal, l.miles) : null);
  const localRange = cheapest && priciest ? { min: Math.min(cheapest.price, priciest.price), max: Math.max(cheapest.price, priciest.price) } : null;

  return { loaded: !!pool, ranked: pool?.sorted.length ?? 0, all, local, nearest, cheapest, priciest, verdictOf, localRange };
}
